import React, { useEffect, useState } from "react";
import {
  getFirestore,
  collection,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { 
  FaTrash, 
  FaEye, 
  FaEdit, 
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaSeedling
} from "react-icons/fa";

import styles from "./field.module.css";
import FieldModal from "./FieldModal";

const ShortField = () => {
  const [error, setError] = useState("");
  const [fields, setFields] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [loading, setLoading] = useState(true);

  const getFieldData = async () => {
    try {
      setLoading(true);
      const auth = getAuth();
      const user = auth.currentUser;
      if (user) {
        const db = getFirestore();
        const fieldsCollectionRef = collection(db, `users/${user.uid}/fields`);
        const querySnapshot = await getDocs(fieldsCollectionRef);
        const fieldsData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setFields(fieldsData);
      } else {
        throw new Error("User must be authenticated to retrieve field data.");
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = async (fieldId, fieldName) => {
    if (window.confirm(`Are you sure you want to delete "${fieldName}"?`)) {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (user) {
          const db = getFirestore();
          await deleteDoc(doc(db, `users/${user.uid}/fields`, fieldId));
          getFieldData();
        }
      } catch (error) {
        setError(error.message);
      }
    }
  };

  const handleUpdate = async (updatedField) => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (user) {
        const db = getFirestore();
        const fieldDocRef = doc(db, `users/${user.uid}/fields`, updatedField.id);
        await updateDoc(fieldDocRef, {
          fieldName: updatedField.fieldName,
          cropName: updatedField.cropName,
          season: updatedField.season,
          sowingDate: updatedField.sowingDate,
          harvestingDate: updatedField.harvestingDate,
          fertilizerApplication: updatedField.fertilizerApplication,
        });
        setFields((prevFields) =>
          prevFields.map((field) =>
            field.id === updatedField.id ? updatedField : field
          )
        );
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const handleDetails = (field) => {
    setSelectedField(field);
  };

  useEffect(() => {
    getFieldData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (fields.length === 0) {
    return (
      <div className="text-center py-8">
        <FaMapMarkerAlt className="text-gray-300 text-4xl mx-auto mb-3" />
        <p className="text-gray-500 text-lg">No fields added yet</p>
        <p className="text-gray-400 text-sm">Get started by adding your first field</p>
      </div>
    );
  }

  return (
    <div className={`h-full w-full rounded-md overflow-y-auto ${styles.coustomScrollBar}`}>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded-lg mb-4">
          {error}
        </div>
      )}
      
      <div className="space-y-3">
        {fields.map((field, key) => (
          <div
            key={key}
            className="group bg-white p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all duration-300"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1">
                <div className="relative">
                  <img
                    src={require("../../assets/crops.jpg")}
                    alt={field.fieldName}
                    className="w-14 h-14 rounded-lg object-cover border-2 border-gray-100 group-hover:border-blue-200 transition-colors"
                  />
                  <div className="absolute -top-1 -right-1 bg-blue-500 text-white p-1 rounded-full">
                    <FaSeedling className="text-xs" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-800 group-hover:text-blue-600 transition-colors">
                    {field.fieldName.split('-')[0]}
                  </h3>
                  {field.cropName && (
                    <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                      <FaSeedling className="text-green-500 text-xs" />
                      {field.cropName}
                    </p>
                  )}
                  {field.sowingDate && (
                    <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                      <FaCalendarAlt className="text-xs" />
                      Sown: {new Date(field.sowingDate).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  className="bg-blue-50 text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-100 transition-colors duration-200 flex items-center gap-2 group/btn"
                  onClick={() => handleDetails(field)}
                >
                  <FaEye className="text-sm" />
                  <span className="hidden sm:inline">Details</span>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(field.id, field.fieldName);
                  }}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200 group/delete"
                  title="Delete field"
                >
                  <FaTrash className="group-hover/delete:scale-110 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {selectedField && (
        <FieldModal
          setflag={setSelectedField}
          onUpdate={handleUpdate}
          fields={selectedField}
        />
      )}
    </div>
  );
};

export default ShortField;